'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEvent,
  CellContextMenuEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
  SortChangedEvent,
} from 'ag-grid-community';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  MetaDefault,
  Spinner,
  TABLE_IDS,
  Table,
  archivedEntityRowCss,
  type TableId,
} from '@terra/ui-design-system';
import { isArchivedLifecycle } from '@terra/shared-dto-types';
import { Database01Icon } from '@terra/ui-icons';
import type {
  AssetPreviewDto,
  AssetsSearchGroupBy,
} from '@terra/shared-contracts-assets';
import { useDomScrollPreservation } from '../../../hooks/useDomScrollPreservation';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useTargetId } from '../../../store/projectStore';
import type { AssetsTableSort } from '../../../store/tab/assetsTableViewTabScopedStore';
import type { AssetsDetectionView } from '../../../store/tab/assetsTabScopedFilterStore';
import {
  ASSETS_TABLE_GROUP_HEADER_ROW_CLASS,
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
  createGroupHeaderStickyClassName,
} from '../../common/list-page-table-theme';
import { ListPageTableBodyLayer } from '../../common/ListPageTableBodyLayer';
import { useListPageTablePersistence } from '../../common/useListPageTablePersistence';
import { EmptyState } from '../../EmptyState';
import { TorchAssetsDiscoveryWaiting } from '../TorchAssetsDiscoveryWaiting';
import { TorchAssetsParametrizationWaiting } from '../TorchAssetsParametrizationWaiting';
import {
  ASSETS_TABLE_KIND,
  isAssetsTableGroupHeaderRow,
  type AssetsTableKind,
  type AssetsTableRow,
} from './assets-table.model';
import {
  isAssetsTableSortColumn,
  useAssetsTableColumnDefs,
} from './useAssetsTableColumnDefs';
import { isRowClickDisabledTarget } from '../rowClickBlocker';

const ASSETS_TABLE_ROW_HEIGHT_PX = 44;
const assetsGroupHeaderStickyClassName = createGroupHeaderStickyClassName({
  rowClass: ASSETS_TABLE_GROUP_HEADER_ROW_CLASS,
  backgroundColor: 'bg.canvas',
});
const ASSETS_TABLE_LOAD_MORE_THRESHOLD_ROWS = 20;
const ASSETS_TABLE_ID_BY_KIND = {
  [ASSETS_TABLE_KIND.ENDPOINTS]: TABLE_IDS.PROXY_ENDPOINT_ASSETS_LIST,
  [ASSETS_TABLE_KIND.WEB_PAGES]: TABLE_IDS.PROXY_WEB_PAGE_ASSETS_LIST,
  [ASSETS_TABLE_KIND.UNVERIFIED]: TABLE_IDS.PROXY_UNVERIFIED_ASSETS_LIST,
} as const satisfies Record<AssetsTableKind, TableId>;
const DEFAULT_COL_DEF: ColDef<AssetsTableRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['asc', 'desc', null],
};

export type AssetsTableProcessingState = {
  isDiscoveryActive: boolean;
  isParametrizationActive: boolean;
  isDeduplicationActive: boolean;
};

type AssetsTableProps = {
  kind: AssetsTableKind;
  rows: AssetsTableRow[];
  loadMoreRowIndex: number | null;
  assetCount: number;
  groupBy: AssetsSearchGroupBy | undefined;
  detectionView: AssetsDetectionView;
  sort: AssetsTableSort | null;
  isLoading: boolean;
  isRefetching: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  hasKnownAssets: boolean;
  processing: AssetsTableProcessingState;
  onLoadMore: () => void;
  onSortChange: (sort: AssetsTableSort | null) => void;
  onOpenAsset: (asset: AssetPreviewDto) => void;
  onOpenAssetContextMenu: ({
    event,
    asset,
  }: {
    event: MouseEvent;
    asset: AssetPreviewDto;
  }) => void;
};

const getAssetsTableRowId = (params: GetRowIdParams<AssetsTableRow>) =>
  params.data.id;

const getAssetsTableRowClass = (params: RowClassParams<AssetsTableRow>) =>
  isAssetsTableGroupHeaderRow(params.data)
    ? ASSETS_TABLE_GROUP_HEADER_ROW_CLASS
    : undefined;

const getAssetsTableRowStyle = (
  params: RowClassParams<AssetsTableRow>,
): RowStyle | undefined => {
  if (isAssetsTableGroupHeaderRow(params.data)) {
    return { cursor: 'default' };
  }

  const asset = params.data;
  const isArchived =
    asset !== undefined &&
    asset.assetType !== 'unverifiedEndpoint' &&
    isArchivedLifecycle(asset.lifecycle);

  return { cursor: 'pointer', ...(isArchived ? archivedEntityRowCss : {}) };
};

function AssetsTableEmptyState({
  isLoading,
  hasKnownAssets,
  isRefetching,
  processing,
}: Pick<
  AssetsTableProps,
  'isLoading' | 'hasKnownAssets' | 'isRefetching' | 'processing'
>) {
  const t = useTranslations('designSystem.applicationAssets.sections');
  const isProcessing =
    processing.isDiscoveryActive ||
    processing.isParametrizationActive ||
    processing.isDeduplicationActive;

  if (!hasKnownAssets && isProcessing) {
    return (
      <ListPageTableBodyLayer>
        {processing.isParametrizationActive ? (
          <TorchAssetsParametrizationWaiting />
        ) : processing.isDeduplicationActive ? (
          <TorchAssetsParametrizationWaiting phase="deduplication" />
        ) : (
          <TorchAssetsDiscoveryWaiting />
        )}
      </ListPageTableBodyLayer>
    );
  }

  if (isLoading || (hasKnownAssets && isRefetching)) {
    return null;
  }

  return (
    <ListPageTableBodyLayer>
      <EmptyState
        icon={Database01Icon}
        title={t('noAssetsFound')}
        description={t('noAssetsFoundDescription')}
      />
    </ListPageTableBodyLayer>
  );
}

export function AssetsTable({
  kind,
  rows,
  loadMoreRowIndex,
  assetCount,
  groupBy,
  detectionView,
  sort,
  isLoading,
  isRefetching,
  isFetchingMore,
  hasMore,
  hasKnownAssets,
  processing,
  onLoadMore,
  onSortChange,
  onOpenAsset,
  onOpenAssetContextMenu,
}: AssetsTableProps) {
  const t = useTranslations('proxy.assets.table');
  const columnDefs = useAssetsTableColumnDefs({
    kind,
    groupBy,
    detectionView,
  });
  const persistence = useListPageTablePersistence(
    ASSETS_TABLE_ID_BY_KIND[kind],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetId = useTargetId();
  const { activePageScrollKey } = useMainContentTabs();
  const scrollId =
    activePageScrollKey !== undefined
      ? `${activePageScrollKey}:assets-table`
      : `assets-table-${targetId ?? 'unknown'}`;
  const { scrollerRef } = useDomScrollPreservation({
    scrollId,
    isRestoreReady: !isLoading || assetCount > 0,
    contentRevision: rows.length,
  });
  const loadMoreIfNearEnd = useCallback(
    (api: GridApi<AssetsTableRow>) => {
      if (!hasMore || isFetchingMore || loadMoreRowIndex === null) {
        return;
      }

      if (
        api.getLastDisplayedRowIndex() >=
        loadMoreRowIndex - ASSETS_TABLE_LOAD_MORE_THRESHOLD_ROWS
      ) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, loadMoreRowIndex, onLoadMore],
  );
  const handleGridReady = useCallback(
    (event: GridReadyEvent<AssetsTableRow>) => {
      event.api.applyColumnState({
        state: sort ? [{ colId: sort.field, sort: sort.order }] : [],
        defaultState: { sort: null },
      });
      scrollerRef(
        containerRef.current?.querySelector<HTMLElement>('.ag-body-viewport') ??
          null,
      );
    },
    [scrollerRef, sort],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<AssetsTableRow>) => {
      const sortedColumn = event.api
        .getColumnState()
        .find((column) => column.sort === 'asc' || column.sort === 'desc');

      if (!sortedColumn || !isAssetsTableSortColumn(sortedColumn.colId)) {
        onSortChange(null);

        return;
      }

      onSortChange({
        field: sortedColumn.colId,
        order: sortedColumn.sort === 'asc' ? 'asc' : 'desc',
      });
    },
    [onSortChange],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<AssetsTableRow>) => {
      if (
        !event.data ||
        isAssetsTableGroupHeaderRow(event.data) ||
        isRowClickDisabledTarget(event.event)
      ) {
        return;
      }

      onOpenAsset(event.data);
    },
    [onOpenAsset],
  );
  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<AssetsTableRow>) => {
      if (
        !event.data ||
        isAssetsTableGroupHeaderRow(event.data) ||
        !(event.event instanceof MouseEvent)
      ) {
        return;
      }

      onOpenAssetContextMenu({ event: event.event, asset: event.data });
    },
    [onOpenAssetContextMenu],
  );

  return (
    <Box
      ref={containerRef}
      flex={1}
      w="full"
      h="full"
      overflow="hidden"
      position="relative"
      className={assetsGroupHeaderStickyClassName}
      css={LIST_PAGE_TABLE_HEADER_CSS}
    >
      <Table
        key={`${kind}:${groupBy ?? 'none'}`}
        columnDefs={columnDefs}
        persistence={persistence}
        rowData={rows}
        getRowId={getAssetsTableRowId}
        rowHeight={ASSETS_TABLE_ROW_HEIGHT_PX}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        onGridReady={handleGridReady}
        onSortChanged={handleSortChanged}
        onBodyScroll={(event: BodyScrollEvent<AssetsTableRow>) =>
          loadMoreIfNearEnd(event.api)
        }
        onRowDataUpdated={(event) => loadMoreIfNearEnd(event.api)}
        onRowClicked={handleRowClicked}
        getRowClass={getAssetsTableRowClass}
        onCellContextMenu={handleCellContextMenu}
        preventDefaultOnContextMenu
        getRowStyle={getAssetsTableRowStyle}
        suppressNoRowsOverlay
      />
      {assetCount === 0 ? (
        <AssetsTableEmptyState
          isLoading={isLoading}
          hasKnownAssets={hasKnownAssets}
          isRefetching={isRefetching}
          processing={processing}
        />
      ) : null}
      {isFetchingMore ? (
        <HStack
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="bg.default"
          boxShadow="sm"
          pointerEvents="none"
        >
          <Spinner size="sm" />
          <MetaDefault color="fg.muted">{t('loadingMore')}</MetaDefault>
        </HStack>
      ) : null}
    </Box>
  );
}
